import { App } from '@df/core/app';
import { ProcessorService } from '@df/ng/processor.service';
import { ItemsInRowOptions } from '@df/ui/atomic/atomic-options';
import { fixJsonString } from '@df/util/fn/fix-json-string';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { NCmsBlogPost } from '../../blog/post/cms-blog-post.interface';
import { NCmsComponentListing } from './cms-component-listing.interface';
import { isCustomRule } from './cms-component-listing.utils';

export const CMS_COMPONENT_LISTING_CONFIG = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
    id: NCmsComponentListing.ID,
    name: 'Listing',
    image: '/common/cms/listing-component.png',
    tags: ['listing', 'big'],
    isAdvanced: true,
    fields: [
        // general
        <NICmsContentField.IField>{
            key: 'section-general',
            type: NICmsContentField.EFieldType.label,
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            description: 'Listed object types.',
            options: [
                { key: 'page', title: 'Page or Post' },
                { key: 'product', title: 'Product' },
                { key: 'category', title: 'Category' },
                { key: 'store', title: 'Store' }
            ],
            default: 'page'
        },
        <NICmsContentField.IChoiceField>{
            key: 'matching',
            type: NICmsContentField.EFieldType.choice,
            title: 'Matching Type',
            description: 'How to pick models to show.',
            options: [
                { key: 'pageType', title: 'By page type' },
                { key: 'group', title: 'By group' },
                { key: 'custom', title: 'Custom' }
            ],
            default: 'pageType',
            canShow: (data: NCmsComponentListing.IData) => data?.type === 'page'
        },
        <NICmsContentField.IChoiceField>{
            key: 'pageType',
            type: NICmsContentField.EFieldType.choice,
            title: 'Page type',
            description: 'How to pick models to show.',
            options: () => Object.values(ICmsContentService.pageTypes).map(i => ({ key: i.id, title: i.title })),
            default: NCmsBlogPost.typeId,
            canShow: (data: NCmsComponentListing.IData) => data?.type === 'page' && data?.matching === 'pageType'
        },
        <NICmsContentField.IChoiceField>{
            key: 'group',
            type: NICmsContentField.EFieldType.choice,
            title: 'Group',
            description: 'How to pick models to show.',
            prepare: () => ProcessorService.injector.get(App).resources.group.loadAll(),
            options: () =>
                ProcessorService.injector
                    .get(App)
                    .resources.group.getAllExisting()
                    .map(i => {
                        return { key: i.id, title: i.name };
                    }),
            canShow: (data: NCmsComponentListing.IData) => data?.type === 'page' && data?.matching === 'group'
        },
        <NICmsContentField.ITextField>{
            key: 'rule',
            type: NICmsContentField.EFieldType.text,
            title: 'Matching Rule',
            description: 'Mongo query to run on data to identify requested models',
            sanitize: (value?: string) => {
                if (value) {
                    const fixed = fixJsonString(value);

                    if (fixed) {
                        return fixed;
                    } else {
                        throw new Error('Invalid JSON');
                    }
                } else {
                    return value;
                }
            },
            default: {},
            canShow: isCustomRule
        },
        <NICmsContentField.IChoiceField>{
            key: 'colspan',
            type: NICmsContentField.EFieldType.choice,
            title: 'Items In Row',
            options: ItemsInRowOptions,
            default: 'c-3-set'
        },
        <NICmsContentField.ITextField>{
            key: 'limit',
            type: NICmsContentField.EFieldType.text,
            title: 'Limit',
            description: 'Max number of items to display',
            sanitize: (value?: string): string | undefined => {
                return value ? value.replace('[D]', '') : value;
            },
            default: 36
        },
        <NICmsContentField.INumberField>{
            key: 'initialStepsVisible',
            type: NICmsContentField.EFieldType.number,
            title: 'Initial Steps loaded',
            description: 'Steps showing up at page load',
            default: 12
        },
        <NICmsContentField.INumberField>{
            key: 'infScrollStep',
            type: NICmsContentField.EFieldType.number,
            title: 'Step Size',
            description: 'Number of items loaded per infinite scroll step.',
            default: 12
        },
        <NICmsContentField.ITextField>{
            key: 'sort',
            type: NICmsContentField.EFieldType.text,
            title: 'Sort Order',
            description: 'Sort order in mongodb syntax like {name : 1, age : -1}',
            sanitize: (value?: string) => {
                if (value) {
                    const fixed = fixJsonString(value);

                    if (fixed) {
                        return fixed;
                    } else {
                        throw new Error('Invalid JSON');
                    }
                } else {
                    return value;
                }
            }
        },
        <NICmsContentField.INumberField>{
            key: 'verbosity',
            type: NICmsContentField.EFieldType.number,
            title: 'Verbosity',
            description: 'How detailed data should be returned',
            default: 2,
            sanitize: (value?: number) => {
                if (value < 1 || value > 4) {
                    throw new Error('Invalid value. Available values: 1, 2, 3 or 4');
                }

                return value;
            }
        }
    ],
    initData: {
        limit: 32,
        initialStepsVisible: 12,
        infScrollStep: 12,
        rule: {}
    }
};
